import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import jwt from 'jwt-decode'
import { ChallengeFailedError, login } from '../../services/auth/login'
import { useAuthContext } from '../../context/auth/auth'
import { useTurnstile } from '../../hooks/useTurnstile'
import Input from '../Formik/Input'
import InputWrapper from '../Formik/InputWrapper'
import Label from '../Formik/Label'
import Button from '../Buttons/Button'
import { navigateTo } from '../../helpers/navigateTo'
import { DecodedTokenTypes } from '../../interfaces/decodedToken'
import Spinner from '../UI/Spinner'
import { useTranslation } from 'react-i18next'
import FormikForm from '../Formik/FormikForm'
import FormikErrorText from '../Formik/FormikErrorText'

const LoginForm = () => {
  const { t, i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const { storeToken } = useAuthContext()
  const turnstile = useTurnstile({
    siteKey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
    action: 'login',
    // The widget speaks ISO 639-1; the detector may report 'el-GR'.
    language: i18n.language?.split('-')[0],
  })

  const initialValues = {
    username: '',
    password: '',
    keepMeSignedIn: true,
  }

  const validationSchema = yup.object({
    username: yup.string().required(),
    password: yup.string().required(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        setIsLoading(true)
        const {
          status,
          data: { data },
        } = await login({
          username: values.username,
          password: values.password,
          getToken: turnstile.getToken,
        })
        if (status === 200) {
          setIsLoading(false)
          const { user_type }: DecodedTokenTypes = jwt(data.access)
          storeToken(data, values.keepMeSignedIn)
          navigate(navigateTo(user_type))
        }
      } catch (error) {
        setIsLoading(false)
        toast.error(
          t(
            error instanceof ChallengeFailedError
              ? 'humanVerificationFailed'
              : 'InvalidUsernameOrPassword',
          ),
        )
      }
    },
  })

  return (
    <FormikForm onSubmitHandler={formik.handleSubmit} formType='lg' paddingX='sm:px-28'>
      <div className='mb-3'>
        <h2 className='font-bold text-center text-3xl mb-4'>{t('welcome')}</h2>
        <p className='text-lightGray text-center'>{t('enterCredentials')}</p>
      </div>

      <InputWrapper>
        <Label text={t('userName')} />
        <Input
          id='Username'
          as='input'
          type='text'
          placeholder={t('insertUsername')}
          hasError={formik.touched.username && formik.errors.username ? true : false}
          {...formik.getFieldProps('username')}
        />
      </InputWrapper>
      <InputWrapper>
        <Label text={t('password')} />
        <Input
          id='Password'
          as='input'
          type='password'
          placeholder={t('insertPassword')}
          hasError={formik.touched.password && formik.errors.password ? true : false}
          {...formik.getFieldProps('password')}
        />
        <Link className='self-end text-primaryBlue text-sm mt-[0.625rem]' to='/forgot-password'>
          {t('forgotPassword')}
        </Link>
        {/* The Turnstile widget sits under the link, input-wide. */}
        <div ref={turnstile.containerRef} />
        {turnstile.hasFailed && <FormikErrorText errorText={t('humanVerificationFailed')} />}
      </InputWrapper>
      <Button
        width='min-w-full'
        type='submit'
        text={isLoading ? 'Loading...' : t('logIn')}
        disabled={isLoading || !turnstile.isVerified}
      >
        {isLoading && <Spinner spinner='form' />}
      </Button>

      <label className='flex items-center gap-2.5 -mt-3 cursor-pointer select-none'>
        <input
          id='keepMeSignedIn'
          type='checkbox'
          className='w-4 h-4 rounded-[4px] border-borderGray text-primaryBlue focus:ring-primaryBlue cursor-pointer'
          checked={formik.values.keepMeSignedIn}
          {...formik.getFieldProps('keepMeSignedIn')}
        />
        <span className='text-sm text-[#3E3E3E]'>{t('keepMeSignedIn')}</span>
      </label>
    </FormikForm>
  )
}

export default LoginForm
